import EChartCustomized, {
    SCHEMES,
    TOOLTIP_OPTIONS,
    useMarkArea,
} from '@/components/EchartCustomized'
import { formatBandwidthUnit, max } from '@/utils/function'
import dayjs from 'dayjs'
import React from 'react'
import { Typogaphy } from 'vnetwork-ui'
import { Bandwidth } from '../../types'

interface BandwidthChartProps {
    bandwidth: Bandwidth
}

const BandwidthChart: React.FC<BandwidthChartProps> = ({ bandwidth }) => {
    const maxBandwidth = max(bandwidth?.data?.map((item) => item?.value)) || 0
    const timestamps = bandwidth?.data?.map((item) =>
        dayjs(item.timestamp)?.format('YYYY-MM-DD')
    )
    const zeroTimestamps = bandwidth?.data
        ?.filter((item) => item.value === 0)
        ?.map((item) => dayjs(item.timestamp)?.format('YYYY-MM-DD'))

    const [markAreaStart, markAreaStop] = useMarkArea(
        timestamps,
        zeroTimestamps
    )
    const options = {
        tooltip: {
            trigger: 'axis',
            ...TOOLTIP_OPTIONS,
            formatter: function (params: any) {
                const [current] = params

                return `
                <b>${dayjs(current?.name)?.format('DD MMM YYYY')}</b> ${current?.value !== 0 ? formatBandwidthUnit(current?.value) : '-'}<br/>
            `
            },
        },
        grid: {
            top: '15%',
            left: -45,
            right: '0%',
            bottom: '0%',
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: timestamps,
            axisLine: {
                lineStyle: {
                    type: [3, 3],
                    dashOffset: 3,
                },
            },
            axisLabel: {
                align: 'left',
                formatter: (value: any) => dayjs(value)?.format('DD MMM'),
            },
        },
        yAxis: {
            type: 'value',
            splitNumber: 2,
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#e0e0e0',
                    type: [3, 3],
                    dashOffset: 3,
                },
            },
            axisLabel: {
                align: 'left',
                margin: 50,
                color: 'black',
                formatter: (value: number) =>
                    formatBandwidthUnit(value, 0)?.replace('.00', ''),
            },
        },
        color: [SCHEMES.default[3]],
        series: [
            {
                data: bandwidth?.data?.map((item) => item.value),
                label: {
                    formatter: ({ value }: any) => formatBandwidthUnit(value),
                },
                type: 'line',
                animation: false,
                showSymbol: false,
                markArea: zeroTimestamps?.length !== 0 && {
                    data: [
                        [
                            {
                                name: 'Updating Data',
                                xAxis: markAreaStart,
                                itemStyle: {
                                    color: '#f0f0f0',
                                },
                                label: {
                                    show: true,
                                    color: '#aaa',
                                    offset: [-125, 7.5],
                                    fontSize: 16,
                                    rotate: 90,
                                },
                            },
                            {
                                xAxis: markAreaStop,
                            },
                        ],
                    ],
                },
                markPoint: {
                    symbol: 'pin',
                    symbolSize: 60,
                    data: [
                        {
                            type: 'max',
                            name: 'Max Bandwidth',
                            itemStyle: {
                                color: SCHEMES.default[3], // đỏ đẹp giống trong hình
                            },
                            label: {
                                formatter: (param: any) =>
                                    formatBandwidthUnit(param?.value),
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: 12,
                                padding: [4, 6],
                            },
                        },
                    ],
                },
            },
        ],
    }
    return (
        <div className="bg-white rounded-[8px] p-4">
            <div className="">
                <Typogaphy size="medium" className="font-semibold text-primary">
                    Bandwidth peak Trends
                </Typogaphy>
            </div>
            {/* @ts-ignore */}
            <EChartCustomized options={options} />
            <div className="py-4">
                <div className="flex items-center gap-2">
                    <div className="inline-block w-[10px] h-[10px] bg-[#d62728]" />
                    <Typogaphy>Bandwidth</Typogaphy>
                </div>
            </div>
            <div className="text-center">
                <div className="border-1 bg-gray-100 px-2 py-1 inline-block">
                    <div className="flex gap-1">
                        <Typogaphy size="small" className="text-gray-500">
                            Max Bandwidth:
                        </Typogaphy>
                        <Typogaphy size="small" className="font-medium">
                            {formatBandwidthUnit(maxBandwidth)}
                        </Typogaphy>
                        <Typogaphy size="small" className="text-gray-500">
                            Average Bandwidth:
                        </Typogaphy>
                        <Typogaphy size="small" className="font-medium">
                            {formatBandwidthUnit(
                                Number(bandwidth?.averageThroughput)
                            )}
                        </Typogaphy>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BandwidthChart
